/**
 * Centralized context checks for preventing cache updates in specific views
 */

/**
 * Check if the user is currently in a context where cache updates should be skipped
 * @param context - The context to check for ('trash' | 'favorites' | 'both')
 * @returns true if cache updates should be skipped, false otherwise
 */
export function shouldSkipCacheUpdates(context: 'trash' | 'favorites' | 'both' = 'both'): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const pathname = window.location.pathname

  switch (context) {
    case 'trash':
      return pathname.startsWith('/dashboard/trash')

    case 'favorites':
      return pathname.startsWith('/dashboard/favorites')

    case 'both':
      return pathname.startsWith('/dashboard/trash') || pathname.startsWith('/dashboard/favorites')

    default:
      return false
  }
}

/**
 * Get a descriptive message for why cache updates are being skipped
 * @param context - The context to check for ('trash' | 'favorites' | 'both')
 * @returns A descriptive message or null if cache updates should not be skipped
 */
export function getSkipCacheMessage(
  _context: 'trash' | 'favorites' | 'both' = 'both',
): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const pathname = window.location.pathname

  if (pathname.startsWith('/dashboard/trash')) {
    return '🚫 Skipping cache update - user is in trash context'
  }

  if (pathname.startsWith('/dashboard/favorites')) {
    return '🚫 Skipping cache update - user is in favorites context'
  }

  return null
}

/**
 * Check if cache updates should be skipped and log the reason
 * @param context - The context to check for ('trash' | 'favorites' | 'both')
 * @param operation - Optional description of the operation being skipped
 * @returns true if cache updates should be skipped, false otherwise
 */
export function shouldSkipCacheUpdatesWithLog(
  context: 'trash' | 'favorites' | 'both' = 'both',
  operation?: string,
): boolean {
  const shouldSkip = shouldSkipCacheUpdates(context)

  if (shouldSkip) {
    const message = getSkipCacheMessage(context)
    const operationText = operation ? ` for ${operation}` : ''

    console.log(`${message}${operationText}`)
  }

  return shouldSkip
}

/**
 * Async version of shouldSkipCacheUpdatesWithLog for use in async functions
 * @param context - The context to check for ('trash' | 'favorites' | 'both')
 * @param operation - Optional description of the operation being skipped
 * @returns Promise<boolean> - true if cache updates should be skipped, false otherwise
 */
export async function shouldSkipCacheUpdatesWithLogAsync(
  context: 'trash' | 'favorites' | 'both' = 'both',
  operation?: string,
): Promise<boolean> {
  return shouldSkipCacheUpdatesWithLog(context, operation)
}
